import * as React from "react";
import { Helmet } from "react-helmet";

import { useHistory, useLocation } from "react-router-dom";
import SearchFiltersSidebarForm from "./SearchFiltersSidebarForm";

import { ThemeProvider } from "styled-components";

import {
  AnalyticsSearchQuery,
  BaseFavouriteBtn,
  Pagination,
  SearchResultStatus,
  useInitialFocus,
  useSearchFilters,
  VolunteerSearchLink,
} from "@skv/core";

import {
  favouritesStore,
  organisationResultStore,
  OrganisationSearchQueryState,
  useOrganisationSearchQuery,
  useThunkDispatch,
} from "@skv/state";

import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import { useUserDispatch } from "~/state";
import {
  ResultCard,
  ResultCardFooter,
  ResultCardHeader,
  ResultCardLinkHeading,
  ResultCardLinkHeadingProps,
  ResultMetaList,
  ResultMetaListItem,
} from "./ResultCard";

interface OrganisationResultCardProps {
  organisationName: string;
  to: ResultCardLinkHeadingProps["to"];
  category: React.ReactNode;
  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;

  isFavourited: boolean;
  onFavourite: () => void;
  onUnfavourite: () => void;
}

const OrganisationResultCard: React.FC<OrganisationResultCardProps> = ({
  organisationName,
  to,
  category,
  headingProps,
  isFavourited,
  onFavourite,
  onUnfavourite,
}) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          title={organisationName}
          to={to}
          {...headingProps}
          stretchedLink={true}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
      </ResultMetaList>

      <ResultCardFooter
        className="d-flex align-items-center"
        css={`
          margin-left: -${(p) => p.theme.btnPaddingX};
        `}
      >
        <ThemeProvider
          theme={(theme) => ({
            ...theme,
            dangerColor: theme.primaryColor,
          })}
        >
          <div
            css={`
              margin-left: -0.4rem !important;
            `}
          >
            <BaseFavouriteBtn
              isFavourited={isFavourited}
              onFavourite={onFavourite}
              onUnfavourite={onUnfavourite}
              type={FAVOURITES_ENUM.ORGANISATION}
            />
          </div>
        </ThemeProvider>
      </ResultCardFooter>
    </ResultCard>
  );
};

const OrganisationSearch: React.FC<Record<string, never>> = () => {
  const history = useHistory();

  const location = useLocation<{
    analytics?: {
      didKeywordAutoComplete: boolean;
      didWhereAutoComplete: boolean;
    };
    searchQuery?: OrganisationSearchQueryState;
  }>();
  const { push } = history;

  const dispatch = useThunkDispatch();

  const { updateUser } = useUserDispatch();

  const searchQuery = useOrganisationSearchQuery();

  const { favourites, handleFavourite, handleUnfavourite } =
    favouritesStore.hooks.useFavourites();

  const { data, loading, error, status } =
    organisationResultStore.hooks.useOrganisationSearchResult(searchQuery);

  const { organisations, page, totalCount, totalPages } = data || {};

  const searchResultsRef = useInitialFocus<HTMLDivElement>({
    shouldFocus: !loading && !error && !!data,
  });

  const { filters, handleFiltersChange } = useSearchFilters({
    searchQuery,
    filters: ["categoryList"],
    onChange: (nextQuery) => {
      dispatch(
        organisationResultStore.actions.fetchOrganisations(nextQuery)
      ).then((data) => {
        push(data.canonicalUrl, {
          searchQuery: nextQuery,
          analytics: location.state?.analytics,
        });
      });
    },
  });

  const handleReset = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    dispatch(organisationResultStore.actions.fetchOrganisations()).then(
      (data) => {
        push(data.canonicalUrl, {
          searchQuery: {},
          analytics: location.state?.analytics,
        });
      }
    );
  };

  React.useEffect(() => {
    /* on server load, set current searchQuery to history state if no current searchQuery */
    if (!location.state?.searchQuery) {
      history.replace(history.location.pathname + history.location.search, {
        searchQuery,
        ...location.state,
      });
    }

    const handlePopState = (e: PopStateEvent): void => {
      dispatch(
        organisationResultStore.actions.fetchOrganisations(
          e.state.state?.searchQuery
        )
      );
    };

    window.addEventListener("popstate", handlePopState, false);

    return (): void => {
      window.removeEventListener("popstate", handlePopState, false);
    };
  }, []);

  React.useEffect(() => {
    /**
     * if at any time a user refreshes the page, the cache clears but their history stack is still there
     * if they navigate their history via browser back/forward, and hit the search results view again,
     * we will be in the below state
     */
    if (status === SearchResultStatus.IDLE && !data) {
      dispatch(organisationResultStore.actions.fetchOrganisations(searchQuery));
    }
  }, [status, data, dispatch, searchQuery]);

  const { category, keyword, where } = searchQuery || {};

  React.useEffect(() => {
    const hasQueries = !!(category?.length || keyword || where);

    if (hasQueries && searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [category, keyword, where, searchResultsRef]);

  return (
    <>
      <Helmet>
        <title>Find volunteering organisations in New South Wales</title>
      </Helmet>
      <div className="row">
        <aside className="col-lg-3 mb-4 mb-lg-0">
          <SearchFiltersSidebarForm
            searchQuery={searchQuery}
            filters={filters}
            onChange={handleFiltersChange}
            onReset={handleReset}
            resetLinkTo={{
              pathname: "/volunteering-organisations",
              state: {},
            }}
          />
        </aside>
        <section
          css={`
            @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
              padding-left: 4rem;
            }
          `}
          className="col-lg-9"
        >
          {((): React.ReactNode => {
            if (loading) {
              return (
                <p role="status" className="mt-3">
                  Loading. Please wait...
                </p>
              );
            }

            if (error) {
              return (
                <p role="status">
                  <strong>Sorry. Something went wrong.</strong>
                  <br />
                  Try reloading the page, or changing your search criteria.
                </p>
              );
            }

            /* no results */
            if (data && !totalCount) {
              return (
                <div className="mt-3">
                  <p>
                    Sorry, we couldn't find any organisations that matched what
                    you were searching for.
                    <br />
                    Maybe try the following...
                  </p>
                  <ul>
                    <li>Change or reset the filters to broaden your search</li>
                    <li>Check the spelling of your search terms</li>
                  </ul>
                  <AnalyticsSearchQuery
                    searchQuery={searchQuery}
                    data={{
                      subSection: "organisation",
                      subSubSection: "organisation:search+results",
                      subSubSubSection: `organisation:search+results:page${page}`,
                      pageTitle: `organisation:search+results:page${page}`,
                      pageType: "organisation:search+results",
                      searchLocationInputMethod: location.state?.analytics
                        ?.didWhereAutoComplete
                        ? "auto-suggest"
                        : "typed",
                    }}
                  />
                </div>
              );
            }

            if (data) {
              return (
                <>
                  <div
                    className="mb-4 mt-3"
                    ref={searchResultsRef}
                    tabIndex={-1}
                  >
                    <p className="h-5">
                      Showing {page * data.pageSize - (data.pageSize - 1)} -{" "}
                      {page * data.pageSize} of {totalCount} organisations
                    </p>
                  </div>

                  <ul className="list-unstyled">
                    {organisations.map((org) => {
                      if (org.url) {
                        return (
                          <li key={org.id} className="my-0">
                            <OrganisationResultCard
                              organisationName={org.name}
                              to={(location) => ({
                                pathname: org.url,
                                state: {
                                  referrer: {
                                    ...location,
                                    state: {
                                      searchQuery,
                                      searchType: "org",
                                      children: "Organisation Search",
                                    },
                                  },
                                },
                              })}
                              headingProps={{ className: "h1" }}
                              category={
                                org.category?.url ? (
                                  <VolunteerSearchLink
                                    className="font-weight-normal text-dark"
                                    to={org.category.url}
                                    searchQuery={{
                                      category: [org.category.value],
                                    }}
                                    searchType="org"
                                    aria-label={`Category: ${org.category.name}`}
                                  >
                                    {org.category.name}
                                  </VolunteerSearchLink>
                                ) : null
                              }
                              isFavourited={!!favourites.organisations[org.id]}
                              onFavourite={() => {
                                handleFavourite(
                                  org.id,
                                  FAVOURITES_ENUM.ORGANISATION
                                );
                                updateUser((prev) => ({
                                  ...prev,
                                  favouriteCount: prev.favouriteCount + 1,
                                }));
                              }}
                              onUnfavourite={() => {
                                handleUnfavourite(
                                  org.id,
                                  FAVOURITES_ENUM.ORGANISATION
                                );
                                updateUser((prev) => ({
                                  ...prev,
                                  favouriteCount: prev.favouriteCount - 1,
                                }));
                              }}
                            />
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                  <Pagination
                    isNsw={true}
                    currentPage={page}
                    totalPages={totalPages}
                    searchQuery={searchQuery}
                    searchType="org"
                  />
                  <AnalyticsSearchQuery
                    searchQuery={searchQuery}
                    data={{
                      subSection: "organisation",
                      subSubSection: "organisation:search+results",
                      subSubSubSection: `organisation:search+results:page${page}`,
                      pageTitle: `organisation:search+results:page${page}`,
                      pageType: "organisation:search+results",
                      searchLocationInputMethod: location.state?.analytics
                        ?.didWhereAutoComplete
                        ? "auto-suggest"
                        : "typed",
                    }}
                  />
                </>
              );
            }
          })()}
        </section>
      </div>
    </>
  );
};

export default OrganisationSearch;
